/*
 * This is mostly lifted from the old STV app, and renders the main page to display the tickets.
 * The main difference to the original is that we have replaced useFeathers with useAxios.
 */

import {
  CircularProgress,
  Container,
  IconButton,
  Paper,
} from "@material-ui/core";
import CloudDownload from "@material-ui/icons/CloudDownload";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import Loading from "../components/Loading";
import TicketsFilters from "../components/TicketsFilters";
import TicketsTable from "../components/TicketsTable";
import { TICKET_API_URL, VIEWS_API_URL } from "../environmentVariables";
import { downloadCSV } from "../helpers/downloadCSV";
import {
  ClientLabel,
  PageProps,
  Query,
  Views,
} from "../helpers/interfaceHelper";
import useAxios from "../hooks/useAxios";
import useDebounced from "../hooks/useDebounced";
import useFilters from "../hooks/useFilters";
import usePersistedState from "../hooks/usePersistedState";
import useQuery from "../hooks/useQuery";
import useView from "../hooks/useView";
import { useAuth } from "../services/authService";

export interface Ticket {
  domain: string;
  id: string;
  client: string;
  subject: string;
  submitter: string;
  status: string;
  priority: string;
  impact: string;
  urgency: string;
  agent: string;
  dateCreated: string;
  dateDue: string;
  dateUpdated: string;
  type: string;
  category: string;
  subCategory: string;
  itemCategory: string;
  kaseyaId: string;
  url: string;
  assignedGroup: string;
  lastModifiedAt: string;
  teamLabel: string;
  breached: boolean;
  quickWin: string;
}

interface Data {
  data: Ticket[];
  skip: number;
  limit: number;
  total: number;
}

export const DEFAULT_TICKET: Ticket = {
  domain: "",
  id: "",
  client: "",
  subject: "",
  submitter: "",
  status: "",
  priority: "",
  impact: "",
  urgency: "",
  agent: "",
  dateCreated: "",
  dateDue: "",
  dateUpdated: "",
  type: "",
  category: "",
  subCategory: "",
  itemCategory: "",
  kaseyaId: "",
  url: "",
  assignedGroup: "",
  lastModifiedAt: "",
  teamLabel: "",
  breached: false,
  quickWin: "",
};

/*
 * Generates the actual page to display the tickets themselves.
 */
const TicketsPage: React.FC<PageProps> = ({ setDarkMode, darkMode }) => {
  const [sortProperty, setSortProperty] = useState("dateDue");
  const [sortDirection, setSortDirection] = useState(1);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [filters, setFilter, resetFilter] = useFilters([]);
  const [search, setSearch] = useState("");
  const [chosenView, setChosenView] = useView();
  const [labelsEnabled, setLabelsEnabled] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [defaultView, setDefaultView] = usePersistedState(
    "Actionable Tickets (SD)",
    "default-ticket-view"
  );
  const { getAccessToken } = useAuth();

  const debouncedSearch = useDebounced(search, 750);

  //retrieve the views from the relevant API and then use the chosen option
  //to construct query parameters
  const [views, viewsLongLoading] = useAxios<Views>(
    `${VIEWS_API_URL}/TicketViews`
  );

  // retrieve list of client labels
  const [clientLabels, clientLabelsLoading] = useAxios<ClientLabel[]>(
    `${TICKET_API_URL}/ClientLabels`
  );

  //when we receive the list of views from the API, we want to make sure the default is selected
  useEffect(() => {
    setChosenView(defaultView, views.views);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views.views]);

  useEffect(
    () =>
      setLabelsEnabled(
        views?.views?.find((v) => v.id == chosenView)?.labelsEnabled ?? false
      ),
    [chosenView, views.views]
  );

  //each time the page is rendered, we want to generate the query to be used to retrieve data from the api
  const query: Query = useQuery({
    sortDirection,
    sortProperty,
    page,
    rowsPerPage,
    filters,
    search: debouncedSearch,
    view: chosenView,
    views: views.views,
  });

  //each time the page is rendered, we want to set the page to page 0 if:
  //the filters, debounced search, or view values have been changed
  useEffect(() => {
    setPage(0);
  }, [filters, debouncedSearch, chosenView]);

  //we now use axios to send the query rather than feathers
  const [axiosData, dataLongLoading] = useAxios<Data>(
    `${TICKET_API_URL}/Tickets`,
    query,
    60000
  );

  //as data is loaded, the loadingData state should be modified. If we are currently loading data,
  //we set the loading state in order to display the loading page
  useEffect(() => {
    setLoading(viewsLongLoading || dataLongLoading || clientLabelsLoading);
  }, [viewsLongLoading, dataLongLoading, clientLabelsLoading]);

  const paging = {
    total: axiosData.total,
    perPage: rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
  };

  const sorting = {
    sortProperty,
    sortDirection,
    setSort: (property: string) => {
      if (property === sortProperty) {
        setSortDirection(sortDirection * -1);
      } else {
        setSortProperty(property);
        setSortDirection(1);
      }
    },
  };

  const downloadCSVClicked = async () => {
    const token = await getAccessToken();
    downloadCSV(
      `TicketsTable${moment().format("YYYYMMDDhhmm")}.csv`,
      `${TICKET_API_URL}/TicketsCSV`,
      query,
      token
    );
  };

  //generate the layout for the page, including the filters and the table of data.
  //pass through any required information such as dark modes, paging and sorting settings
  return (
    <>
      {isLoading && <Loading />}
      <Layout
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        drawer={
          <TicketsFilters
            setFilter={setFilter}
            resetFilter={resetFilter}
            dbViews={views.views}
            view={chosenView}
            setView={setChosenView}
            setDefaultView={setDefaultView}
            search={search}
            setSearch={setSearch}
            query={query}
            labelsEnabled={labelsEnabled}
          />
        }
      >
        <Paper style={{ overflow: "auto" }}>
          {!isLoading &&
          axiosData.data !== undefined &&
          views.views !== undefined ? (
            <>
              <div style={{ width: "100%", textAlign: "right" }}>
                <IconButton
                  color="inherit"
                  aria-label="download csv"
                  title="download csv"
                  onClick={() => downloadCSVClicked()}
                  style={{ marginRight: "36px" }}
                >
                  <CloudDownload></CloudDownload>
                </IconButton>
              </div>

              <div style={{ width: "100%" }}>
                <TicketsTable
                  tickets={axiosData.data}
                  paging={paging}
                  sorting={sorting}
                  labelsEnabled={labelsEnabled}
                  clientLabels={clientLabels}
                />
              </div>
            </>
          ) : (
            <Container
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress style={{ margin: "10px" }} />
            </Container>
          )}
        </Paper>
      </Layout>
    </>
  );
};

export default TicketsPage;
